import { z } from 'zod';

// === Slide Schemas ===

// --- Canvas element schemas ---

const imageFiltersSchema = z.object({
  brightness: z.number().min(0).max(200).optional(),
  contrast: z.number().min(0).max(200).optional(),
  saturation: z.number().min(0).max(200).optional(),
  blur: z.number().min(0).max(20).optional(),
  grayscale: z.number().min(0).max(100).optional(),
});

const gradientStopSchema = z.object({
  offset: z.number().min(0).max(100),
  color: z.string(),
});

const gradientFillSchema = z.object({
  type: z.enum(['linear', 'radial']),
  angle: z.number().optional(),
  stops: z.array(gradientStopSchema).min(2),
});

const baseElementSchema = z.object({
  id: z.string().uuid(),
  x: z.number(),
  y: z.number(),
  width: z.number().positive(),
  height: z.number().positive(),
  rotation: z.number().min(0).max(360),
  opacity: z.number().min(0).max(1),
  zIndex: z.number().int(),
  locked: z.boolean().optional(),
});

const textElementSchema = baseElementSchema.extend({
  type: z.literal('text'),
  content: z.string(),
  fontFamily: z.string(),
  fontSize: z.number().positive(),
  fontWeight: z.number().int().min(100).max(900),
  fontStyle: z.enum(['normal', 'italic']),
  fontColor: z.string(),
  textAlign: z.enum(['left', 'center', 'right']),
  textDecoration: z.enum(['none', 'underline', 'line-through', 'underline line-through']),
  lineHeight: z.number().min(0.9).max(2.5),
  letterSpacing: z.number().min(-0.05).max(0.3),
  textTransform: z.enum(['none', 'uppercase', 'lowercase']),
  textEffect: z.lazy(() => textEffectSchema).nullable().optional(),
  bgColor: z.string().optional(),
  bgPadding: z.number().min(0).optional(),
  autoHeight: z.boolean().optional(),
});

const imageElementSchema = baseElementSchema.extend({
  type: z.literal('image'),
  src: z.string(),
  fit: z.enum(['cover', 'contain', 'stretch', 'none']),
  filters: imageFiltersSchema.optional(),
  cornerRadius: z.number().min(0).optional(),
  flipH: z.boolean().optional(),
  flipV: z.boolean().optional(),
});

const shapeElementSchema = baseElementSchema.extend({
  type: z.literal('shape'),
  shape: z.enum(['rect', 'ellipse', 'triangle', 'line', 'diamond']),
  fill: z.string(),
  fillGradient: gradientFillSchema.optional(),
  stroke: z.string().optional(),
  strokeWidth: z.number().min(0).optional(),
  cornerRadius: z.number().min(0).optional(),
});

export const slideElementSchema = z.discriminatedUnion('type', [
  textElementSchema,
  imageElementSchema,
  shapeElementSchema,
]);

// --- Content + Style ---

export const slideContentSchema = z.object({
  mainText: z.string(),
  subText: z.string().optional(),
  bibleRef: z
    .object({
      book: z.string(),
      chapter: z.number().int().positive(),
      verse: z.number().int().positive(),
      verseTo: z.number().int().positive().optional(),
    })
    .optional(),
  elements: z.array(slideElementSchema).optional(),
  videoUrl: z.string().url().optional(),
  videoAutoplay: z.boolean().optional(),
});

export const bgPatternSchema = z.object({
  preset: z.enum([
    'linear-gradient', 'radial-vignette', 'mesh-soft', 'dots-pattern',
    'aurora', 'pulse-glow', 'slow-shift', 'noise-grain',
    'mesh-flow', 'bokeh-drift', 'prism-conic',
  ]),
  colors: z.array(z.string()).min(2).max(4),
  speed: z.number().int().min(1).max(10).optional(),
  angle: z.number().int().min(0).max(360).optional(),
});

export const textEffectSchema = z.object({
  preset: z.enum(['shadow', 'glow', 'outline', 'neon', 'emboss']),
  color: z.string(),
  intensity: z.number().int().min(1).max(10),
});

export const slideStyleSchema = z.object({
  bgColor: z.string().default('#000000'),
  bgImage: z.string().nullable().optional(),
  bgPattern: bgPatternSchema.nullable().optional(),
  bgOverlayColor: z.string().optional(),
  bgOverlayOpacity: z.number().min(0).max(1).optional(),
  fontColor: z.string().default('#FFFFFF'),
  fontSize: z.string().default('4xl'),
  fontFamily: z.string().default('inter'),
  fontWeight: z.number().int().min(100).max(900).optional(),
  fontStyle: z.enum(['normal', 'italic']).optional(),
  textAlign: z.enum(['left', 'center', 'right']).default('center'),
  lineHeight: z.number().min(0.9).max(2.5).optional(),
  letterSpacing: z.number().min(-0.05).max(0.3).optional(),
  textEffect: textEffectSchema.nullable().optional(),
  textTransform: z.enum(['none', 'uppercase', 'lowercase']).optional(),
  transition: z.enum(['cut', 'fade', 'slide-up', 'zoom', 'none', 'slide']).default('fade'),
  transitionDurationMs: z.number().int().min(0).max(1000).optional(),
  transitionTextDelayMs: z.number().int().min(0).max(500).optional(),
  canvasMode: z.boolean().optional(),
});

export const slideTypeSchema = z.enum(['title', 'verse', 'song', 'blank', 'image', 'video']);

export const createSlideSchema = z.object({
  presentationId: z.string().uuid(),
  type: slideTypeSchema,
  content: slideContentSchema,
  style: slideStyleSchema.optional(),
  orderNum: z.number().int().min(0).optional(),
  groupId: z.string().uuid().nullable().optional(),
  sectionId: z.string().uuid().nullable().optional(),
});

// === Slide Group Schemas ===

export const createSlideGroupSchema = z.object({
  presentationId: z.string().uuid(),
  label: z.string().min(1).max(255),
  orderNum: z.number().int().min(0).optional(),
});

export const updateSlideGroupSchema = z.object({
  label: z.string().min(1).max(255).optional(),
  orderNum: z.number().int().min(0).optional(),
});

export const reorderSlideGroupsSchema = z.object({
  groupIds: z.array(z.string().uuid()).min(1),
});

// === Slide Section Schemas ===

export const createSlideSectionSchema = z.object({
  groupId: z.string().uuid(),
  label: z.string().min(1).max(255),
  orderNum: z.number().int().min(0).optional(),
});

export const updateSlideSectionSchema = z.object({
  label: z.string().min(1).max(255).optional(),
  orderNum: z.number().int().min(0).optional(),
});

export const reorderSlideSectionsSchema = z.object({
  sectionIds: z.array(z.string().uuid()).min(1),
});

export const updateSlideSchema = z.object({
  type: slideTypeSchema.optional(),
  content: slideContentSchema.optional(),
  style: slideStyleSchema.optional(),
  orderNum: z.number().int().min(0).optional(),
  sectionId: z.string().uuid().nullable().optional(),
});

// === Presentation Schemas ===

export const createPresentationSchema = z.object({
  name: z.string().min(1).max(255),
  category: z.string().max(100).optional(),
});

export const updatePresentationSchema = z.object({
  name: z.string().min(1).max(255).optional(),
  category: z.string().max(100).optional(),
});

// === Song Schemas ===

export const songSectionTypeSchema = z.enum([
  'verse',
  'chorus',
  'bridge',
  'pre_chorus',
  'intro',
  'outro',
]);

export const createSongSchema = z.object({
  title: z.string().min(1).max(255),
  author: z.string().max(255).optional(),
  category: z.string().max(100).optional(),
  tags: z.string().max(500).optional(),
  sections: z
    .array(
      z.object({
        type: songSectionTypeSchema,
        label: z.string().min(1),
        lyrics: z.string().min(1),
        orderNum: z.number().int().min(0),
      }),
    )
    .optional(),
});

export const updateSongSchema = z.object({
  title: z.string().min(1).max(255).optional(),
  author: z.string().max(255).optional(),
  category: z.string().max(100).optional(),
  tags: z.string().max(500).optional(),
});

export const createSongSectionSchema = z.object({
  songId: z.string().uuid(),
  type: songSectionTypeSchema,
  label: z.string().min(1),
  lyrics: z.string().min(1),
  orderNum: z.number().int().min(0),
});

export const updateSongSectionSchema = z.object({
  type: songSectionTypeSchema.optional(),
  label: z.string().min(1).optional(),
  lyrics: z.string().min(1).optional(),
});

export const createArrangementSchema = z.object({
  name: z.string().min(1).max(255),
  sectionOrder: z.array(z.string().uuid()),
});

export const updateArrangementSchema = z.object({
  name: z.string().min(1).max(255).optional(),
  sectionOrder: z.array(z.string().uuid()).optional(),
}).refine((d) => d.name !== undefined || d.sectionOrder !== undefined, {
  message: 'Al menos un campo requerido',
});

// === Bible Search ===

export const bibleSearchSchema = z.object({
  q: z.string().min(1).max(200),
});

// === Reorder Schema ===

export const reorderSlidesSchema = z.object({
  slideIds: z.array(z.string().uuid()),
});

export const renameGroupSchema = z.object({
  label: z.string().min(1).max(255),
});

export const applyStyleSchema = z.object({
  slideIds: z.array(z.string().uuid()).min(1),
  style: slideStyleSchema,
  sourceElements: z.array(slideElementSchema).optional(),
});

export const copySlidesSchema = z.object({
  targetPresentationId: z.string().uuid(),
  slideIds: z.array(z.string().uuid()).min(1).max(200),
  groupLabel: z.string().min(1).max(255).optional(),
});

// === Font Schemas ===

export const createFontFamilySchema = z.object({
  label: z.string().min(1).max(100),
});

export const fontFileMetaSchema = z.object({
  weight: z.number().int().min(100).max(900).default(400),
  style: z.enum(['normal', 'italic']).default('normal'),
  variable: z.boolean().default(false),
});

// === Org Schemas ===

export const createOrganizationSchema = z.object({
  name: z.string().min(1).max(100),
  slug: z.string().min(1).max(50).regex(/^[a-z0-9-]+$/, 'slug: solo minúsculas, números y guiones'),
});

export const updateOrganizationSchema = z.object({
  name: z.string().min(1).max(100).optional(),
  plan: z.enum(['free', 'pro']).optional(),
});

export const inviteMemberSchema = z.object({
  email: z.string().email(),
  role: z.enum(['admin', 'editor']),
});

// === Sync Schemas ===

const SYNC_TABLES = [
  'presentations', 'slides', 'slide_groups', 'slide_sections',
  'songs', 'song_sections',
  'song_arrangements', 'custom_fonts', 'custom_font_files', 'uploaded_images',
] as const;

export const syncOpSchema = z.object({
  table: z.enum(SYNC_TABLES),
  recordId: z.string().min(1),
  operation: z.enum(['upsert', 'delete']),
  payload: z.record(z.unknown()),
  clientTimestamp: z.number().int().positive(),
});

export const syncPushSchema = z.object({
  ops: z.array(syncOpSchema).min(1).max(500),
});
